import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, FileOutput, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MarkdownRenderer — Renders markdown with interactive section blocks.
 * 
 * Each heading creates a new "block" with hover actions:
 * - Copy section text
 * - Add section to Report Builder
 * 
 * This makes every part of the AI response independently actionable.
 */

function SectionBlock({ children, sectionText, onAddToReport }) {
  const [hovered, setHovered] = useState(false);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(sectionText || '').then(() => toast.success('Section copied')).catch(() => {
      const ta = document.createElement('textarea'); ta.value = sectionText || ''; ta.style.cssText = 'position:fixed;left:-9999px'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); toast.success('Section copied');
    });
  }, [sectionText]);

  const handleAddToReport = useCallback((e) => {
    e.stopPropagation();
    onAddToReport?.(sectionText);
    toast.success('Added to report');
  }, [sectionText, onAddToReport]);

  return (
    <div
      className={`relative ${onAddToReport ? 'group/section -mx-2 px-2 rounded-lg transition-colors duration-150 hover:bg-muted/20' : ''}`}
      onMouseEnter={() => onAddToReport && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Hover toolbar - only in chat context when onAddToReport is provided */}
      {hovered && onAddToReport && (
        <div className="absolute -left-1 top-0 flex flex-col gap-0.5 opacity-0 group-hover/section:opacity-100 transition-opacity duration-150 z-10">
          <button
            onClick={handleCopy}
            className="w-6 h-6 rounded flex items-center justify-center bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Copy section"
          >
            <Copy className="w-3 h-3" />
          </button>
          <button
            onClick={handleAddToReport}
            className="w-6 h-6 rounded flex items-center justify-center bg-muted/80 hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors"
            title="Add to report"
          >
            <FileOutput className="w-3 h-3" />
          </button>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-muted/80 text-muted-foreground/40 cursor-grab" title="Drag to report">
            <GripVertical className="w-3 h-3" />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export function MarkdownRenderer({ content, onAddToReport }) {
  if (!content) return null;

  // Split content into sections by headings for block-level interactivity
  const sections = splitIntoSections(content);

  if (sections.length <= 1) {
    // Single block — no section splitting needed
    return (
      <SectionBlock sectionText={content} onAddToReport={onAddToReport}>
        <div className="markdown-content pl-2">
          <RawMarkdown content={content} />
        </div>
      </SectionBlock>
    );
  }

  return (
    <div className="markdown-content space-y-1">
      {sections.map((section, i) => (
        <SectionBlock key={i} sectionText={section.text} onAddToReport={onAddToReport}>
          <div className="pl-2">
            <RawMarkdown content={section.text} />
          </div>
        </SectionBlock>
      ))}
    </div>
  );
}

function splitIntoSections(content) {
  if (!content) return [];
  const lines = content.split('\n');
  const sections = [];
  let current = { text: '', hasHeading: false };

  for (const line of lines) {
    if (/^#{1,4}\s/.test(line) && current.text.trim()) {
      sections.push({ ...current });
      current = { text: line + '\n', hasHeading: true };
    } else {
      current.text += line + '\n';
      if (/^#{1,4}\s/.test(line)) current.hasHeading = true;
    }
  }
  if (current.text.trim()) sections.push(current);
  return sections;
}

function RawMarkdown({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 className="text-xl font-bold mt-4 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-lg font-bold mt-3 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-base font-semibold mt-3 mb-1.5" style={{ fontFamily: 'Space Grotesk' }}>{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-sm font-semibold mt-2 mb-1" style={{ fontFamily: 'Space Grotesk' }}>{children}</h4>
        ),
        p: ({ children }) => (
          <p className="text-sm leading-7 text-foreground/90 mb-2 last:mb-0">{children}</p>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-foreground">{children}</strong>
        ),
        em: ({ children }) => (
          <em className="italic text-foreground/80">{children}</em>
        ),
        ul: ({ children }) => (
          <ul className="list-disc list-outside space-y-1 my-2 text-sm leading-7 text-foreground/90 ml-5">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-outside space-y-1 my-2 text-sm leading-7 text-foreground/90 ml-5">{children}</ol>
        ),
        li: ({ children }) => (
          <li className="text-sm leading-7 pl-1">{children}</li>
        ),
        code: ({ node, children, className, ...props }) => {
          // Check if it's a block code (has language- class from ```language blocks)
          const match = /language-(\w+)/.exec(className || '');
          if (match || (node?.parent?.tagName === 'pre') || String(children).includes('\n')) {
            return (
              <div className="relative my-3">
                {match && (
                  <span className="absolute top-2 right-2 text-[9px] font-mono px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground">{match[1]}</span>
                )}
                <pre className="text-[11px] font-mono bg-muted/20 rounded-lg p-4 overflow-x-auto leading-5 border border-border/30">
                  <code>{children}</code>
                </pre>
              </div>
            );
          }
          // Inline code
          return (
            <code className="text-[11px] font-mono bg-muted/40 px-1 py-0.5 rounded text-foreground/90 break-words">
              {children}
            </code>
          );
        },
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {children}
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-primary/30 pl-4 my-2 text-sm text-foreground/80 bg-primary/5 py-2 rounded-r-lg">
            {children}
          </blockquote>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-3 rounded-lg border border-border/30">
            <table className="w-full text-xs">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-muted/30 border-b border-border/30">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="px-3 py-2 text-left font-semibold font-mono text-[11px]">{children}</th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-1.5 font-mono text-[11px] border-t border-border/10">{children}</td>
        ),
        hr: () => <hr className="my-4 border-border/30" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
